import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Correzione Ufficio di RUOLO e SEDE OPERATIVA di una prestazione già registrata.
// Le opzioni (ruoli + sedi) arrivano da /work-entries/{id}/correction-options: STESSE fonti della
// timbratura (SERVICE_ROLES + Sedi operative dell'appalto). I campi compaiono solo quando il servizio
// li prevede davvero; nessun elenco parallelo. Lo stato vive nel `corr` del dialog padre.
export function EntryRoleSiteCorrection({ entry, corr, onChange }) {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get(`/work-entries/${entry.id}/correction-options`).then((r) => {
      if (cancel) return;
      setOpts(r.data);
      onChange({
        role: r.data.current.role_or_task || "",
        siteId: r.data.current.operative_site_id || "",
        opts: r.data,
      });
    }).catch(() => {});
    return () => { cancel = true; };
  }, [entry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!opts) return null;
  const showRole = opts.role_editable && Array.isArray(opts.roles);
  const isCasa = opts.site_mode === "casa_asa";
  const role = (corr && corr.role != null) ? corr.role : (opts.current.role_or_task || "");
  const showSite = opts.site_mode === "entity" || (isCasa && role === opts.casa_role_asa);
  const hasSites = opts.sites.length > 0;

  if (!showRole && opts.site_mode === "none") return null;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="corr-rolesite">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Ruolo / Sede operativa</p>
      {showRole && (
        <div>
          <Label className="text-xs text-slate-500">Ruolo</Label>
          <Select value={role} onValueChange={(v) => onChange({ ...corr, role: v, opts })}>
            <SelectTrigger data-testid="corr-role-select" className="h-10"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>{opts.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {showSite && (
        <div>
          <Label className="text-xs text-slate-500">Sede operativa{isCasa && hasSites ? " · obbligatoria" : ""}</Label>
          {hasSites ? (
            <>
              <Select value={(corr && corr.siteId) || ""} onValueChange={(v) => onChange({ ...corr, siteId: v, opts })}>
                <SelectTrigger data-testid="corr-site-select" className="h-10"><SelectValue placeholder="Seleziona la sede…" /></SelectTrigger>
                <SelectContent>{opts.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              {isCasa && !(corr && corr.siteId) && (
                <p className="text-xs text-amber-600 mt-1" data-testid="corr-site-required">
                  Per il ruolo ASA / OSS è necessario selezionare la sede operativa.
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-500" data-testid="corr-site-none">
              Nessuna sede operativa configurata: la prestazione resta sul contesto generale.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// true se la correzione non è ancora salvabile (sede obbligatoria non selezionata).
export function correctionIncomplete(edit) {
  const corr = (edit && edit.corr) || {};
  const opts = corr.opts;
  if (!opts) return false;
  if (opts.site_mode === "casa_asa") {
    const role = (opts.role_editable ? corr.role : null) || edit.entry.role_or_task;
    if (role === opts.casa_role_asa && opts.sites.length > 0 && !corr.siteId) return true;
  }
  if (opts.site_mode === "entity" && opts.sites.length > 0 && !corr.siteId) return true;
  return false;
}

// Costruisce il payload per POST /work-entries/{id}/correct a partire dallo stato del dialog.
// Mantiene orari/data invariati (usa edit.start/edit.end) e aggiunge SOLO ruolo e/o sede corretti.
export function buildCorrectionPayload(edit) {
  const corr = edit.corr || {};
  const opts = corr.opts;
  const payload = {
    start_time: edit.start,
    end_time: edit.end,
    reason: (edit.reason || "").trim(),
    role_or_task: (opts && opts.role_editable) ? (corr.role || null) : (edit.entry.role_or_task || null),
    from_export: true,
  };
  if (opts) {
    if (opts.site_mode === "entity") {
      if (corr.siteId) payload.entity_id = corr.siteId;
    } else if (opts.site_mode === "casa_asa") {
      const role = (opts.role_editable ? corr.role : null) || edit.entry.role_or_task;
      if (role === opts.casa_role_asa) {
        payload.entity_kind = opts.sites.length > 0 ? "operative_site" : "appalto";
        if (opts.sites.length > 0 && corr.siteId) payload.entity_id = corr.siteId;
      } else {
        payload.entity_kind = "appalto";
      }
    }
  }
  return payload;
}
